import React, { useContext, useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Menu,
  MenuItem,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  TextField,
  Badge,
  Tooltip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import PostAddIcon from "@mui/icons-material/PostAdd";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios"; // Import axios if you’re using it.
import logo from "../Assests/Images/Logo.png";
import API_BASE_URL from "../../src/config"; // Update this if necessary for your API base URL

const Navbar = () => {
  const { user, setUser } = useContext(UserContext);
  const isAdmin = user?.role === "admin";
  const location = useLocation();
  const [postAnchor, setPostAnchor] = useState(null);
  const openPostMenu = (event) => setPostAnchor(event.currentTarget);
  const closePostMenu = () => setPostAnchor(null);

  const [adminAnchor, setAdminAnchor] = useState(null);
  const openAdminMenu = (event) => setAdminAnchor(event.currentTarget);
  const closeAdminMenu = () => setAdminAnchor(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCity, setSelectedCity] = useState("");
  const [cityEditable, setCityEditable] = useState(true);

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotificationBox, setShowNotificationBox] = useState(false); // State to show notification box

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const autoDetectCity = async () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const response = await axios.get(
              `https://geocode.maps.co/reverse?lat=${latitude}&lon=${longitude}`
            );
            const detectedCity = response.data.address?.city || "";
            if (detectedCity) {
              setSelectedCity(detectedCity);
              setCityEditable(false);
            } else {
              setCityEditable(true);
            }
          } catch (error) {
            console.error("❌ Error fetching city:", error);
            setCityEditable(true);
          }
        },
        (error) => {
          console.error("❌ Geolocation error:", error);
          setCityEditable(true);
        }
      );
    } else {
      setCityEditable(true);
    }
  };

  useEffect(() => {
    autoDetectCity();
  }, []);

  // Fetch notifications for the user
  useEffect(() => {
    const fetchNotifications = async () => {
      if (user?.id) {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/services/notifications/${user.id}`
          );
          setNotifications(response.data);
          // Count unread notifications
          const unread = response.data.filter(
            (notification) => notification.status === "active"
          );
          setUnreadCount(unread.length);
          // Show the notification box if there are unread notifications
          if (unread.length > 0) {
            setShowNotificationBox(true);
          } else {
            setShowNotificationBox(false);
          }
        } catch (error) {
          console.error("❌ Error fetching notifications:", error);
        }
      }
    };

    fetchNotifications();
  }, [user?.id]);

  // Mark notification as read
  const handleNotificationClick = (notificationId) => {
    axios
      .post(`${API_BASE_URL}/services/notifications/update`, {
        notification_id: notificationId,
        action: "accepted",
      })
      .then(() => {
        // Update local state to mark as read
        setNotifications((prevNotifications) =>
          prevNotifications.map((notif) =>
            notif.id === notificationId
              ? { ...notif, status: "accepted" }
              : notif
          )
        );
        // Update unread count
        setUnreadCount((prevCount) => prevCount - 1);
      })
      .catch((error) => {
        console.error("❌ Error marking notification as read:", error);
      });
  };

  const opportunities = [
    { label: "Post Job", path: "/post-job", enabled: user?.post_job },
    {
      label: "Post Service",
      path: "/post-service",
      enabled: user?.post_service,
    },
    { label: "Post Ad", path: "/post-ad", enabled: user?.post_ad },
    { label: "Post Offer", path: "/post-offer", enabled: user?.post_offer },
    { label: "Post Event", path: "/post-event", enabled: user?.post_event },
    {
      label: "Post Classified",
      path: "/post-classified",
      enabled: user?.post_classified,
    },
  ];

  const accessibleOpportunities = opportunities.filter(
    (opportunity) => opportunity.enabled
  ); // Only show opportunities the user has access to

  const navigate = useNavigate(); // useNavigate hook for navigation

  return (
    <>
      <AppBar
        position="static"
        sx={{ background: `#6E5B9A`, boxShadow: "none", width: "100%" }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          <img src={logo} alt="CityZoom Logo" style={{ height: "80px" }} />

          <IconButton
            color="inherit"
            onClick={toggleDrawer(true)}
            sx={{ display: { xs: "block", md: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 2 }}>
            <Button
              sx={{
                color: location.pathname === "/" ? "white" : "black",
                backgroundColor: location.pathname === "/" ? "black" : "transparent",
                borderBottom: "3px solid cream",
                borderRadius: "5px",
                fontWeight:"bold",
              }}
              component={Link}
              to="/"
            >
              Home
            </Button>

            <Button
              sx={{
                color: location.pathname === "/aboutus" ? "white" : "black",
                backgroundColor: location.pathname === "/aboutus" ? "black" : "transparent",
                borderBottom: "3px solid cream",
                borderRadius: "5px",
                fontWeight:"bold",
              }}
              component={Link}
              to="/aboutus"
            >
              About
            </Button>

            <Button
              sx={{
                color: location.pathname === "/explore" ? "white" : "black",
                backgroundColor: location.pathname === "/explore" ? "black" : "transparent",
                borderBottom: "3px solid cream",
                borderRadius: "5px",
                fontWeight:"bold",
              }}
              component={Link}
              to="/explore"
            >
              Explore
            </Button>

            <Button
             sx={{
              color: location.pathname === "/contact" ? "white" : "black",
              backgroundColor: location.pathname === "/contact" ? "black" : "transparent",
              borderBottom: "3px solid cream",
              borderRadius: "5px",
              fontWeight:"bold",
            }}
              component={Link}
              to="/contact"
            >
              Contact
            </Button>
          </Box>

          {/* <TextField
            size="small"
            placeholder="Search......"
            value={selectedCity}
            onChange={handleChange}
            select
            disabled={!cityEditable}
            InputProps={{
              startAdornment: (
                <IconButton edge="start" color="inherit" sx={{ color: 'black' }}>
                  <SearchIcon sx={{ color: 'black' }} />
                </IconButton>
              ),
              style: { color: 'black' },
            }}
            sx={{
              width: 200,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'black',
                },
                '&:hover fieldset': {
                  borderColor: 'black',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'black',
                },
                '& input': {
                  color: 'black',
                },
              },
            }}
          >
            {cities.map((city, index) => (
              <MenuItem key={index} value={city}>
                {city}
              </MenuItem>
            ))}
          </TextField> */}

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, ml: 2 }}>
            {user ? (
              <>
                <IconButton sx={{ color: "black" }} onClick={openPostMenu}>
                  <PostAddIcon />
                </IconButton>

                <Menu
                  anchorEl={postAnchor}
                  open={Boolean(postAnchor)}
                  onClose={closePostMenu}
                >
                  {accessibleOpportunities.map((opportunity, index) => (
                    <MenuItem
                      key={index}
                      component={Link}
                      to={opportunity.path}
                      onClick={closePostMenu}
                    >
                      {opportunity.label}
                    </MenuItem>
                  ))}
                </Menu>

                {isAdmin && (
                  <>
                    <IconButton sx={{ color: "black" }} onClick={openAdminMenu}>
                      <AdminPanelSettingsIcon />
                    </IconButton>
                    <Menu
                      anchorEl={adminAnchor}
                      open={Boolean(adminAnchor)}
                      onClose={closeAdminMenu}
                    >
                      <MenuItem
                        component={Link}
                        to="/admin"
                        onClick={closeAdminMenu}
                      >
                        Dashboard
                      </MenuItem>
                      <MenuItem
                        component={Link}
                        to="/admin/payments"
                        onClick={closeAdminMenu}
                      >
                        Payments
                      </MenuItem>
                      <MenuItem
                        component={Link}
                        to="/admin/configuration"
                        onClick={closeAdminMenu}
                      >
                        Configuration
                      </MenuItem>
                      <MenuItem
                        component={Link}
                        to="/admin/support"
                        onClick={closeAdminMenu}
                      >
                        My Support Tickets
                      </MenuItem>
                      {/* <MenuItem component={Link} to="/admin/pending-approvals" onClick={closeAdminMenu}>Approvals</MenuItem> */}
                      <MenuItem
                        component={Link}
                        to="/admin/bookings"
                        onClick={closeAdminMenu}
                      >
                        Manage Bookings
                      </MenuItem>
                    </Menu>
                  </>
                )}

                <IconButton sx={{ color: "black" }} onClick={handleMenuClick}>
                  <AccountCircleIcon />
                </IconButton>

                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  <MenuItem
                    onClick={handleMenuClose}
                    component={Link}
                    to="/profile"
                  >
                    Profile
                  </MenuItem>
                  <MenuItem
                    onClick={handleMenuClose}
                    component={Link}
                    to="/opportunities"
                  >
                    Opportunities
                  </MenuItem>
                  <MenuItem
                    onClick={handleMenuClose}
                    component={Link}
                    to="/my-bookings"
                  >
                    My Bookings
                  </MenuItem>
                  <MenuItem
                    onClick={handleMenuClose}
                    component={Link}
                    to="/my-opportunities"
                  >
                    My Opportunities
                  </MenuItem>
                  <MenuItem
                    onClick={handleMenuClose}
                    component={Link}
                    to="/payment-history"
                  >
                    Payment History
                  </MenuItem>
                  <MenuItem
                    onClick={handleMenuClose}
                    component={Link}
                    to="/locationandinterests"
                  >
                    Location And Interests
                  </MenuItem>
                  <MenuItem
                    onClick={handleMenuClose}
                    component={Link}
                    to="/support"
                  >
                    Support
                  </MenuItem>
                  <MenuItem
                    onClick={handleMenuClose}
                    component={Link}
                    to="/referafriend"
                  >
                    Refer a friend
                  </MenuItem>
                    {user?.role === "user" && (
                    <MenuItem
                      onClick={handleMenuClose}
                      component={Link}
                      to="/mytickets"
                      >
                      My Support Tickets
                    </MenuItem>
                    )}
                </Menu>

                {/* Notifications Icon with Badge */}
                <Tooltip title="You have notifications">
                  <IconButton
                    sx={{ color: "black" }}
                    onClick={() => navigate("/notifications")}
                  >
                    <NotificationsIcon />
                    {unreadCount > 0 && (
                      <Badge badgeContent={unreadCount} color="error" />
                    )}
                  </IconButton>
                </Tooltip>

                <Button
                  
                  sx={{ color: "black",backgroundColor:"white",fontWeight:"bold" }}
                  onClick={() => setUser(null)}
                >
                  Logout
                </Button>
              </>
            ) : (
              <Button
              variant="contained"
              sx={{
                backgroundColor: "White",
                border: "2px solid black", // Corrected here
                color: "black",
                fontWeight:"bold",
                "&:hover": {
                  backgroundColor: "black",
                border: "2px solid black", // Corrected here
                color: "white",
                },
              }}
                component={Link}
                to="/login"
              >
                Login
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
        <List>
          <ListItem
            button
            component={Link}
            to="/"
            onClick={toggleDrawer(false)}
            sx={{ color: "white" }}
          >
            <ListItemText primary="Home" />
          </ListItem>
          <ListItem
            button
            component={Link}
            to="/aboutus"
            onClick={toggleDrawer(false)}
            sx={{ color: "white" }}
          >
            <ListItemText primary="About" />
          </ListItem>
          <ListItem
            button
            component={Link}
            to="/explore"
            onClick={toggleDrawer(false)}
            sx={{ color: "white" }}
          >
            <ListItemText primary="Explore" />
          </ListItem>
          <ListItem
            button
            component={Link}
            to="/contact"
            onClick={toggleDrawer(false)}
            sx={{ color: "white" }}
          >
            <ListItemText primary="Contact" />
          </ListItem>
        </List>
      </Drawer>
    </>
  );
};

export default Navbar;
